/**
 * AI CONTEXT
 * Purpose: semester management table (settings + direct route usage).
 * Key deps: semesterAtoms/useAllDocs, Semester mapper, AddSemesterFormModal.
 * Referenced by: Settings tab; also App ROUTES.SEMESTERS.
 * Importance: semester boundaries drive schedule/evaluation grouping.
 * Refactor: avoid length===0 loading heuristic; add consistent row edit behavior.
 */
import { Button } from "antd"
import { useMemo, useState } from "react"

import { semesterAtoms } from "#atoms"
import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import { buildTableColumns } from "#blocks/Table/buildTableColumns"
import Table2 from "#blocks/Table/Table2"
import { Semester } from "#classes/Semester"
import {
  SEMESTER_SORTABLE_FIELDS,
  SEMESTER_TABLE_COLUMNS,
} from "#constants/semesterOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  SemesterConstructor,
  SemesterForTable,
} from "#customTypes/composedTypes/semesterTypes"
import { SETTINGS_KEYS } from "#customTypes/globalTypes"
import AddSemesterFormModal from "#forms/AddSemester/AddSemesterFormModal"
import { useTableSort } from "#hooks/dataHooks/useTableSort"
import { useAllDocs } from "#hooks/jotai/useSubscription"
import useHasPermission, {
  useResourcePermissions,
} from "#hooks/newHooks/useHasPermission"

const SemestersTable = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedSemester, setSelectedSemester] = useState<
    SemesterConstructor | undefined
  >(undefined)

  const { canRead } = useResourcePermissions(RESOURCES.SEMESTER)
  const canViewId = useHasPermission(RESOURCES.ID, USER_ACTIONS.READ)

  const { sortField, sortOrder, sortRows, onSortChange } =
    useTableSort<SemesterForTable>({
      tableKey: SETTINGS_KEYS.SEMESTERS,
      sortableFields: SEMESTER_SORTABLE_FIELDS,
    })

  const semesters = useAllDocs<SemesterConstructor>(
    semesterAtoms,
    DOC_TYPES.SEMESTERS
  )

  const isLoading = semesters.length === 0

  const columns = useMemo(
    () =>
      buildTableColumns<SemesterForTable>(SEMESTER_TABLE_COLUMNS, {
        sortableFields: SEMESTER_SORTABLE_FIELDS,
        showId: canViewId,
      }),
    [canViewId]
  )

  const semestersForTable = useMemo(() => {
    if (!semesters || !canRead) return []

    const rows = semesters.map((semester) => ({
      ...new Semester(semester).getSemesterForTable(),
      lastUpdatedAt: semester.lastUpdatedAt,
    }))
    return sortRows(rows)
  }, [semesters, canRead, sortRows])

  const handleOpen = () => {
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedSemester(undefined)
  }

  if (!canRead) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  return (
    <Page>
      <Flex col style={{ padding: 20, width: "100%", maxWidth: 2200 }} gap={12}>
        <h1>Semesters</h1>
        <Flex>
          <Button onClick={handleOpen}>Add semester</Button>
        </Flex>

        {isOpen && (
          <AddSemesterFormModal
            onClose={handleClose}
            initialValues={selectedSemester}
          />
        )}

        <Table2<SemesterForTable>
          data={semestersForTable}
          columns={columns}
          isLoading={isLoading}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
        />
      </Flex>
    </Page>
  )
}

export default SemestersTable
